import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
}
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);
  return createPortal(<AnimatePresence data-id="element-586" data-mp="2i1mki82pr85e">
      {open ? <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-587" data-mp="2n3bynwl8g1xp">
          <motion.div className="absolute inset-0 bg-navy-900/45" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} onClick={onClose} aria-hidden="true" data-id="element-588" data-mp="2861dfvisfhos" />
          <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} initial={{
        opacity: 0,
        y: 16,
        scale: 0.99
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.99
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} className="relative w-full max-w-lg rounded-t-2xl bg-white shadow-raised outline-none sm:rounded-2xl" data-id="element-589" data-mp="2446cwt8ol8c2">
            <div className="flex items-start gap-4 border-b border-navy-100 px-5 py-4" data-id="element-590" data-mp="3n3nr6uk3cufy">
              <div className="min-w-0 flex-1" data-id="element-591" data-mp="3ibm6qlcrrxeu">
                <h2 className="text-base font-semibold text-navy-900" data-id="element-592" data-mp="3i9t1naht1l8y">
                  {title}
                </h2>
                {description ? <p className="mt-1 text-sm text-navy-500" data-id="element-593" data-mp="38ehmws6ece17">{description}</p> : null}
              </div>
              <button type="button" onClick={onClose} aria-label="Close dialog" className="-mr-1 -mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg text-navy-400 hover:bg-navy-50 hover:text-navy-700" data-id="element-594" data-mp="9urd7whv3pdf">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-595" data-mp="3s0xf25ru0y1o" />
              </button>
            </div>
            {children ? <div className="max-h-[60vh] overflow-y-auto px-5 py-4" data-id="element-596" data-mp="2km142feu4i0u">
                {children}
              </div> : null}
            {footer ? <div className="flex flex-col-reverse gap-2 border-t border-navy-100 px-5 py-4 sm:flex-row sm:justify-end" data-id="element-597" data-mp="w5srt8wg7lqh">
                {footer}
              </div> : null}
          </motion.div>
        </div> : null}
    </AnimatePresence>, document.body);
}